<template>
  <div class="custom-vin-inspection container-fluid p-3">
    <h5 class="mb-1">Create VIN Only Inspection</h5>
    <p class="text-muted mb-3">
      Create an ADAS inspection from a VIN and an RO number, with no estimate. The systems and
      calibrations are predicted from the vehicle, the same as ADAS Map's own VIN lookup.
    </p>

    <!-- A super admin's own firm is an admin firm with no ADAS Map shop behind it, so they name the
         provider they are acting for. Everyone else goes to their own resolved target and cannot aim
         this anywhere: the destination comes from the token, never from the form. -->
    <div v-if="isSuperAdmin" class="custom-firm-picker mb-2">
      <label class="custom-label mb-0">Create for firm</label>
      <Multiselect
        v-model="selectedFirm"
        class="form-control custom-multiselect"
        placeholder="search firm by name"
        :close-on-select="true"
        :filter-results="false"
        :min-chars="1"
        :resolve-on-load="false"
        :delay="400"
        :searchable="true"
        :options="async (query) => await fetchLocations(query)"
        label="firmName"
        value-prop="firm"
        @change="firmChanged"
      />
    </div>

    <div v-if="target.firmName" class="text-muted small mb-2">
      Creating in: <strong>{{ target.firmName }}</strong>
    </div>

    <div v-if="errorMessage" class="alert alert-warning py-2">{{ errorMessage }}</div>

    <!-- Result: the inspection exists in ADAS Map from here on, so this replaces the form. -->
    <div v-if="created" class="alert alert-success py-2">
      Inspection <strong>{{ created.adasInspection }}</strong> created for VIN {{ vin }}.
      <a href="#" class="ms-2" @click.prevent="showResult = true">View results</a>
      <a href="#" class="ms-2" @click.prevent="reset">Create another</a>
    </div>

    <div v-else>
      <div class="row g-2 custom-form">
        <div class="col-12">
          <CustomerShopSection v-model="shop" :shops="shops" :disabled="busy"
            :name-missing="shopNameMissing" :contact-missing="!shopNameMissing && shopContactMissing" />
        </div>

        <div class="col-6">
          <label class="custom-label">VIN <span class="text-danger">*</span></label>
          <input v-model.trim="vin" class="form-control form-control-sm" maxlength="17"
            :class="{ 'is-invalid': vinInvalid || vinCheckFailed }" placeholder="17-character VIN" :disabled="busy" />
          <div v-if="vinInvalid" class="custom-field-error">A 17-character VIN is required.</div>
          <div v-else-if="vinCheckFailed" class="custom-field-error">{{ vinCheckMessage }}</div>
        </div>
        <div class="col-6">
          <label class="custom-label">RO Number <span class="text-danger">*</span></label>
          <input v-model.trim="roNumber" class="form-control form-control-sm"
            :class="{ 'is-invalid': roMissing }" :disabled="busy" />
          <div v-if="roMissing" class="custom-field-error">Required for provider submitted inspections</div>
        </div>
        <div class="col-12">
          <label class="custom-label">Notes</label>
          <textarea v-model="notes" class="form-control form-control-sm" rows="2" :disabled="busy"></textarea>
        </div>
      </div>

      <!-- ADAS Map answers an ambiguous VIN with a list of vehicles instead of an inspection. Choosing
           one re-posts the same request with its manufacturer code. -->
      <div v-if="choices.length" class="custom-choices mt-3">
        <div class="fw-bold mb-1">This VIN matches more than one vehicle. Choose one:</div>
        <!-- Field names as ADAS Map's own chooser reads them (year/make/model/trimName/versionName,
             picked by manufacturerCode). -->
        <div v-for="choice in choices" :key="choice.manufacturerCode" class="custom-choice">
          <a href="#" @click.prevent="submit(choice.manufacturerCode)">
            {{ choice.year }} {{ choice.make }} {{ choice.model }}
            <span class="text-muted">{{ [choice.trimName, choice.versionName].filter((p) => p).join(' ') }}</span>
          </a>
        </div>
      </div>

      <div class="mt-3">
        <button class="btn btn-primary btn-sm" :disabled="busy" @click="submit(null)">
          <span v-if="busy" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          {{ busy ? 'Creating…' : 'Create Inspection' }}
        </button>
      </div>
    </div>
    <DialogInspectionResult :show="showResult" :source="created && created.source"
      :adas-inspection="created && created.adasInspection" @close="showResult = false" />
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import Multiselect from '@vueform/multiselect'
import { authStore } from '@/stores/auth'
import { estimateStore } from '@/stores/estimate'
import { customerStore } from '@/stores/customer'
import { locationStore } from '@/stores/location'
import { vinCheckDigit } from '@/plugins/vin'
import CustomerShopSection, { emptyShop } from '@/components/opportunity/forms/CustomerShopSection.vue'
import DialogInspectionResult from '@/components/opportunity/forms/DialogInspectionResult.vue'
export default {
  name: 'VinInspectionView',
  components: { Multiselect, CustomerShopSection, DialogInspectionResult },
  data() {
    return {
      vin: '',
      roNumber: '',
      notes: '',
      shop: emptyShop(),
      shops: [],
      choices: [],
      created: null,
      showResult: false,
      target: {},
      selectedFirm: null,
      busy: false,
      showErrors: false,
      errorMessage: ''
    }
  },
  computed: {
    ...mapState(authStore, ['profile']),
    isSuperAdmin() {
      return !!(this.profile && this.profile.roles && this.profile.roles[0] === 1)
    },
    vinInvalid() {
      return this.showErrors && (this.vin || '').length !== 17
    },
    // 17 characters but not a valid VIN: bad characters (I, O, Q) or a wrong check digit.
    vinCheckFailed() {
      const v = (this.vin || '').toUpperCase()
      return this.showErrors && v.length === 17 && vinCheckDigit(v) !== v[8]
    },
    vinCheckMessage() {
      const expected = vinCheckDigit(this.vin)
      return expected === null
        ? 'VIN contains invalid characters (I, O and Q are not used in VINs).'
        : 'VIN check digit (9th character) does not match, so the VIN likely has a typo. Check it against the vehicle.'
    },
    roMissing() {
      return this.showErrors && !(this.roNumber || '').trim()
    },
    shopNameMissing() {
      return this.showErrors && !(this.shop.shopName || '').trim()
    },
    shopContactMissing() {
      return this.showErrors && !(this.shop.email || '').trim() && !(this.shop.phone || '').trim()
    }
  },
  async mounted() {
    await this.loadContext()
  },
  methods: {
    ...mapActions(estimateStore, ['getTarget', 'createVinInspection']),
    ...mapActions(customerStore, ['getShops']),
    ...mapActions(locationStore, ['searchLocations']),
    async fetchLocations(query) {
      // searchLocations builds its query string from these exact keys.
      return await this.searchLocations({ search: query, type: null, firmType: null, firm: this.profile.firm })
    },
    async firmChanged() {
      this.errorMessage = ''
      // Saved shops belong to the firm, so a pick from the previous firm no longer applies.
      this.shop = emptyShop()
      await this.loadContext()
    },
    async loadContext() {
      const firm = this.isSuperAdmin ? this.selectedFirm : null
      this.target = {}
      try { this.target = await this.getTarget(firm) } catch (e) {
        const d = e && e.response && e.response.data
        const err = (d && (d.error || d)) || null
        if (err && (err.message || err.Message)) this.errorMessage = err.message || err.Message
      }
      // The shops belong to the firm the inspection is being created for.
      try { this.shops = await this.getShops(null, firm) } catch (e) { this.shops = [] }
    },
    reset() {
      this.vin = ''
      this.roNumber = ''
      this.notes = ''
      this.shop = emptyShop()
      this.choices = []
      this.created = null
      this.showResult = false
      this.showErrors = false
      this.errorMessage = ''
    },
    // manufacturerCode is only set on the second pass, after the user picks from vehicleChoices.
    async submit(manufacturerCode) {
      this.showErrors = true
      this.errorMessage = ''
      if (this.vinInvalid || this.vinCheckFailed || this.roMissing || this.shopNameMissing || this.shopContactMissing) return

      this.busy = true
      try {
        const result = await this.createVinInspection({
          vin: this.vin,
          roNumber: this.roNumber,
          notes: this.notes || null,
          manufacturerCode: manufacturerCode || null,
          firm: this.isSuperAdmin ? this.selectedFirm : null,
          customerShop: this.shop.shopName ? this.shop : null
        })
        if (result && result.error) {
          this.errorMessage = result.error
        } else if (result && result.vehicleChoices) {
          this.choices = result.vehicleChoices
        } else if (result && result.adasInspection) {
          this.created = result
          this.choices = []
          this.showResult = true
          // Pick up a shop this submission created, so the next one can reuse it.
          const firm = this.isSuperAdmin ? this.selectedFirm : null
          try { this.shops = await this.getShops(null, firm) } catch (e) { /* list only */ }
        } else {
          this.errorMessage = 'ADAS Map did not return an inspection for this VIN.'
        }
      } catch (e) {
        const d = e && e.response && e.response.data
        const err = (d && (d.error || d)) || null
        this.errorMessage = (err && (err.message || err.Message)) || 'The inspection could not be created.'
      } finally {
        this.busy = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
}
.custom-form {
  max-width: 900px;
}
.custom-firm-picker {
  width: 320px;
}
.custom-field-error {
  color: #dc3545;
  font-size: 0.8rem;
}
.custom-choices {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  max-width: 900px;
}
.custom-choice {
  padding: 2px 0;
}
</style>
